import React, { useEffect, useRef, useState } from "react";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Barcode from "./pages/Barcode";
import Calendar from "./pages/Calendar";
import Vass from "./pages/Vass";
import Nvr from "./pages/Nvr";
import NvrRun from "./pages/NvrRun";
import Edit from "./pages/Edit";
import AddBarcode from "./pages/AddBarcode";
import ChangePassword from "./pages/ChangePassword";

import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import GlobalStyles from "./css/GlobalStyles.js"; // Import your global styles
import { isBlackUIViewState } from "./stores/isBlackUIViewState";
import { useRecoilState, useRecoilValue } from "recoil";
import { ThemeProvider } from "styled-components";
import { colorWhite, colorDark } from "./css/palette.js";
import { loginState } from "./stores/loginState.js";
import {
  isKpcCompany,
  isKpcDailyLoginExpired,
} from "./utils/kpc.js";
import {
  createAuthenticatedKpcLoginState,
  createLoggedOutKpcLoginState,
  hasKpcAutoLoginCredentials,
  initializeKpcAuthentication,
} from "./api/kpcAuth.js";

// 저장된 KPC 로그인 키가 있으면 메인 API보다 먼저 새 세션을 발급받는다.
// 인증이 끝나기 전에는 업무 화면을 마운트하지 않아 초기 401 경쟁을 방지한다.
function KpcAuthenticationGate({ children }) {
  const [login, setLogin] = useRecoilState(loginState);
  const shouldInitializeRef = useRef(
    isKpcCompany(login.company) && hasKpcAutoLoginCredentials(),
  );
  const [authReady, setAuthReady] = useState(!shouldInitializeRef.current);

  useEffect(() => {
    if (!shouldInitializeRef.current) {
      return undefined;
    }

    let active = true;

    initializeKpcAuthentication()
      .then((user) => {
        if (!active) {
          return;
        }

        if (user) {
          window.sessionStorage.removeItem("kpc-session-expired");
          setLogin((previousLogin) =>
            createAuthenticatedKpcLoginState(previousLogin, user),
          );
          return;
        }

        setLogin((previousLogin) =>
          createLoggedOutKpcLoginState(previousLogin),
        );
      })
      .finally(() => {
        if (active) {
          setAuthReady(true);
        }
      });

    return () => {
      active = false;
    };
  }, [setLogin]);

  if (!authReady) {
    return (
      <div
        style={{
          alignItems: "center",
          display: "flex",
          height: "100vh",
          justifyContent: "center",
        }}
      >
        KPC 로그인 정보를 확인하고 있습니다.
      </div>
    );
  }

  return children;
}

// KPC만 로컬 로그인 날짜를 검사해 만료된 업무 화면이 렌더링되는 것을 사전에 차단한다.
function KpcProtectedRoute({ children, isKpcFeatureEnabled = true }) {
  const login = useRecoilValue(loginState);

  // 다른 택배사는 기존 라우팅과 로그인 정책을 그대로 사용한다.
  if (!isKpcCompany(login.company)) {
    return children;
  }

  // 영일 전산에는 수정 내용을 반영할 수 없으므로 KPC의 업무수정 화면 접근을 차단한다.
  if (!isKpcFeatureEnabled) {
    return <Navigate to="/Dashboard" replace />;
  }

  // 로그인 날짜가 지났다면 API Hook이 있는 페이지를 마운트하지 않고 로그인 화면으로 보낸다.
  if (isKpcDailyLoginExpired(login, hasKpcAutoLoginCredentials())) {
    return (
      <Navigate
        to="/"
        replace
        state={{ kpcSessionExpired: true }}
      />
    );
  }

  // KPC 로그아웃 상태에서 주소를 직접 입력해 업무 화면에 접근하는 것도 차단한다.
  if (!login.isLogin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// 브라우저 탭을 자정 이후까지 열어 둔 경우에도 다시 활성화하는 즉시 당일 로그인을 검사한다.
function KpcDailySessionWatcher() {
  const login = useRecoilValue(loginState);
  const navigate = useNavigate();

  useEffect(() => {
    const redirectExpiredKpcLogin = () => {
      if (isKpcDailyLoginExpired(login, hasKpcAutoLoginCredentials())) {
        navigate("/", {
          replace: true,
          state: { kpcSessionExpired: true },
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        redirectExpiredKpcLogin();
      }
    };

    window.addEventListener("pageshow", redirectExpiredKpcLogin);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pageshow", redirectExpiredKpcLogin);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [login, navigate]);

  return null;
}

function App() {
  const isBlackUI = useRecoilValue(isBlackUIViewState);
  const theme = isBlackUI ? colorDark : colorWhite;
  document.title = "Vass";
  return (
    <div>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <GlobalStyles />
          <KpcAuthenticationGate>
            <KpcDailySessionWatcher />
            <Routes>
              <Route path="/" element={<Login />} />
              <Route
                path="/Dashboard"
                element={
                  <KpcProtectedRoute>
                    <Dashboard theme={theme} />
                  </KpcProtectedRoute>
                }
              />
              <Route
                path="/Barcode"
                element={
                  <KpcProtectedRoute>
                    <Barcode theme={theme} />
                  </KpcProtectedRoute>
                }
              />
              <Route
                path="/AddBarcode"
                element={
                  <KpcProtectedRoute>
                    <AddBarcode theme={theme} />
                  </KpcProtectedRoute>
                }
              />
              <Route
                path="/Edit"
                element={
                  <KpcProtectedRoute isKpcFeatureEnabled={false}>
                    <Edit theme={theme} />
                  </KpcProtectedRoute>
                }
              />
              <Route
                path="/Calendar"
                element={
                  <KpcProtectedRoute>
                    <Calendar theme={theme} />
                  </KpcProtectedRoute>
                }
              />
              <Route
                path="/Vass"
                element={
                  <KpcProtectedRoute>
                    <Vass theme={theme} />
                  </KpcProtectedRoute>
                }
              />
              <Route
                path="/Nvr"
                element={
                  <KpcProtectedRoute>
                    <Nvr theme={theme} />
                  </KpcProtectedRoute>
                }
              />
              <Route
                path="/NvrRun"
                element={
                  <KpcProtectedRoute>
                    <NvrRun theme={theme} />
                  </KpcProtectedRoute>
                }
              />
              <Route
                path="/ChangePassword"
                element={
                  <KpcProtectedRoute>
                    <ChangePassword />
                  </KpcProtectedRoute>
                }
              />
            </Routes>
          </KpcAuthenticationGate>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
